import { useMemo, useState } from "react";
import { Badge, Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { currentMonth, formatMonthLabel, getMonthGridDays, shiftMonth } from "@/features/events/eventDate";
import { useParties, useWeekendSlotSummary } from "@/api/hooks/useParties";
import { BOOKING_STATUS_BADGE_VARIANT, BOOKING_STATUS_LABELS } from "@/api/types/booking";
import { PARTY_OCCASION_LABELS } from "@/api/types/party";
import { CreatePartyDialog } from "./CreatePartyDialog";
import { PartyDetailDialog } from "./PartyDetailDialog";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_PARTY_SLOTS_PER_WEEKEND = 6;

/**
 * Parties — a month grid highlighting each weekend's 6-slot meter (booked
 * / 6, from GET /v2/parties/weekends), plus a flat list of parties in the
 * visible month. New party / party detail are dialogs, same "calendar +
 * dialogs" shape as features/bookings/BookingsPage.tsx.
 */
export function PartiesPage() {
  const [month, setMonth] = useState(currentMonth());
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState<string | undefined>(undefined);
  const [detailPartyId, setDetailPartyId] = useState<string | null>(null);

  const days = useMemo(() => getMonthGridDays(month), [month]);
  const dateFrom = days[0]?.iso;
  const dateTo = days[days.length - 1]?.iso;

  const { data: weekends, isLoading: weekendsLoading, error: weekendsError, refetch: refetchWeekends } =
    useWeekendSlotSummary(dateFrom ?? "", dateTo ?? "");
  const weekendByStart = useMemo(() => {
    const map = new Map<string, { booked: number; remaining: number }>();
    for (const w of weekends ?? []) map.set(w.weekend_start, w);
    return map;
  }, [weekends]);

  const { data: parties, isLoading, error, refetch } = useParties({ date_from: dateFrom, date_to: dateTo, limit: 100 });

  const partiesByDay = useMemo(() => {
    const map = new Map<string, typeof parties>();
    for (const p of parties ?? []) {
      if (p.status === "cancelled") continue;
      const list = map.get(p.booking_date) ?? [];
      list.push(p);
      map.set(p.booking_date, list);
    }
    return map;
  }, [parties]);

  /** Saturday date this cell's weekend meter is keyed on — Sunday reads
   * its own preceding Saturday's summary, matching
   * app/services/party_service.py::weekend_start. */
  function weekendStartFor(iso: string, dow: number): string | null {
    if (dow === 6) return iso; // Saturday
    if (dow === 0) {
      const d = new Date(iso + "T00:00:00");
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    }
    return null;
  }

  function openCreate(dateIso?: string) {
    setCreateDate(dateIso);
    setCreateOpen(true);
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Parties</h1>
        <Button size="sm" onClick={() => openCreate()} data-testid="new-party-button">
          New party
        </Button>
      </div>

      <div className="mb-4 flex items-center justify-center gap-4">
        <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">
          ← Prev
        </Button>
        <h2 className="min-w-[10rem] text-center text-lg font-semibold text-text-primary" data-testid="parties-month-label">
          {formatMonthLabel(month)}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month">
          Next →
        </Button>
      </div>

      <QueryStateBoundary isLoading={isLoading || weekendsLoading} error={error ?? weekendsError} onRetry={() => { void refetch(); void refetchWeekends(); }}>
        <div className="overflow-x-auto">
          <div className="grid min-w-[640px] grid-cols-7 gap-px rounded-lg border border-border bg-border">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="bg-bg-hover px-2 py-1 text-center text-xs font-semibold uppercase text-text-muted">
                {label}
              </div>
            ))}
            {days.map((day) => {
              const dow = day.date.getDay();
              const isWeekend = dow === 0 || dow === 6;
              const dayParties = partiesByDay.get(day.iso) ?? [];
              const weekendStart = isWeekend ? weekendStartFor(day.iso, dow) : null;
              const summary = weekendStart ? weekendByStart.get(weekendStart) : undefined;

              return (
                <div
                  key={day.iso}
                  data-day={day.iso}
                  onClick={() => isWeekend && openCreate(day.iso)}
                  className={`min-h-[6.5rem] p-1.5 ${isWeekend ? "cursor-pointer bg-brass/[0.04] hover:bg-brass/[0.08]" : "bg-bg-card"} ${
                    day.inMonth ? "" : "opacity-40"
                  } ${day.isToday ? "ring-2 ring-inset ring-primary" : ""}`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-text-secondary">{day.date.getDate()}</span>
                    {isWeekend && summary && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          summary.remaining === 0 ? "bg-danger-light text-danger" : "bg-brass/15 text-brass"
                        }`}
                        data-testid={`weekend-meter-${weekendStart}`}
                      >
                        {summary.booked}/{MAX_PARTY_SLOTS_PER_WEEKEND}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {(dayParties ?? []).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailPartyId(p.id);
                        }}
                        className="block w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-bg-hover"
                        data-party-chip={p.id}
                      >
                        <Badge variant={BOOKING_STATUS_BADGE_VARIANT[p.status]} size="sm" className="mr-1">
                          {BOOKING_STATUS_LABELS[p.status]}
                        </Badge>
                        {PARTY_OCCASION_LABELS[p.occasion]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </QueryStateBoundary>

      <div className="mt-8">
        <h2 className="mb-3 font-display text-lg font-semibold text-text-primary">This month's parties</h2>
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {!parties || parties.length === 0 ? (
            <EmptyState
              title="No parties this month"
              description="Book a party on any weekend date to get started."
              action={{ label: "New party", onClick: () => openCreate() }}
            />
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-bg-card" data-testid="parties-list">
              {parties
                .filter((p) => p.status !== "cancelled")
                .sort((a, b) => a.booking_date.localeCompare(b.booking_date))
                .map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setDetailPartyId(p.id)}
                      className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-bg-hover sm:flex-row sm:items-center sm:justify-between"
                      data-party-row={p.id}
                    >
                      <div>
                        <p className="font-medium text-text-primary">
                          {p.resource_name} · {PARTY_OCCASION_LABELS[p.occasion]}
                        </p>
                        <p className="text-xs text-text-muted">
                          {p.booking_date} · {p.slot_label ?? "—"}
                          {p.extra_hour ? " (+1 hr)" : ""}
                          {p.headcount ? ` · ${p.headcount} kids` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">{centsToUsd(p.total_price_cents)}</span>
                        <Badge variant={BOOKING_STATUS_BADGE_VARIANT[p.status]}>{BOOKING_STATUS_LABELS[p.status]}</Badge>
                      </div>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </QueryStateBoundary>
      </div>

      <CreatePartyDialog open={createOpen} onClose={() => setCreateOpen(false)} defaultDate={createDate} />
      <PartyDetailDialog partyId={detailPartyId} onClose={() => setDetailPartyId(null)} />
    </div>
  );
}
